import React, { useState, useEffect } from "react";
import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Security as PermissionsIcon,
  Visibility as ViewIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Code as JsonIcon,
  Lock as SystemIcon,
} from "@mui/icons-material";
import type { Permission } from "@/types/auth";
import { permissionService } from "@/services/permissionService";

/**
 * Tabla de permisos con CRUD completo
 *
 * REGLAS APLICADAS:
 * - Solo <Stack> para layout (NO Grid)
 * - Componentes con size="small"
 * - dense={true} en tabla
 * - Máximo 500 líneas
 */

interface PermissionTableProps {
  onEdit: (permission: Permission) => void;
  onDelete: (permissionId: string) => void;
  onViewStructure: (permission: Permission) => void;
  onToggleStatus: (permissionId: string, newStatus: boolean) => void;
}

export function PermissionTable({
  onEdit,
  onDelete,
  onViewStructure,
  onToggleStatus,
}: PermissionTableProps) {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    permissionId: string;
    permissionName: string;
    isSystemPermission: boolean;
  }>({
    open: false,
    permissionId: "",
    permissionName: "",
    isSystemPermission: false,
  });
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  // Cargar permisos
  useEffect(() => {
    loadPermissions();
  }, []);

  const loadPermissions = async () => {
    try {
      setLoading(true);
      setError(null);

      // Usar el servicio de permisos con autenticación automática
      const data = await permissionService.getAllPermissions();
      setPermissions(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error desconocido");
      console.error("Error cargando permisos:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (permission: Permission) => {
    setDeleteDialog({
      open: true,
      permissionId: permission.id,
      permissionName: permission.nombre,
      isSystemPermission: permission.nombre === "all",
    });
  };

  const handleDeleteConfirm = async () => {
    try {
      await onDelete(deleteDialog.permissionId);
      setDeleteDialog({
        open: false,
        permissionId: "",
        permissionName: "",
        isSystemPermission: false,
      });
      await loadPermissions(); // Recargar lista
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error eliminando permiso");
    }
  };

  const handleToggleStatus = async (
    permissionId: string,
    currentStatus: boolean,
  ) => {
    try {
      await onToggleStatus(permissionId, !currentStatus);
      await loadPermissions(); // Recargar lista
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Error cambiando estado del permiso",
      );
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const countPermissionRoutes = (estructura: Record<string, any>): number => {
    if (!estructura || typeof estructura !== "object") return 0;
    return Object.keys(estructura).length;
  };

  const renderPermissionStructure = (estructura: Record<string, any>) => {
    if (!estructura || typeof estructura !== "object") {
      return <Typography variant="body2">Sin estructura definida</Typography>;
    }

    return (
      <Stack spacing={1}>
        {Object.entries(estructura).map(([ruta, ruteData]) => (
          <Box key={ruta}>
            <Typography variant="subtitle2" color="primary">
              {ruta}
            </Typography>
            {typeof ruteData === "object" &&
              ruteData !== null &&
              ruteData.menus && (
                <Box>
                  {Object.entries(ruteData.menus as Record<string, any>).map(
                    ([menu, menuData]) => (
                      <Box key={menu}>
                        <Typography variant="body2">Carpeta {menu}</Typography>
                        {typeof menuData === "object" &&
                          menuData !== null &&
                          "funciones" in menuData &&
                          menuData.funciones && (
                            <Box>
                              {Object.entries(
                                menuData.funciones as Record<string, boolean>,
                              ).map(([funcion, valor]) => (
                                <Chip
                                  key={funcion}
                                  size="small"
                                  label={funcion}
                                  color={Boolean(valor) ? "success" : "default"}
                                  variant={
                                    Boolean(valor) ? "filled" : "outlined"
                                  }
                                />
                              ))}
                            </Box>
                          )}
                      </Box>
                    ),
                  )}
                </Box>
              )}
          </Box>
        ))}
      </Stack>
    );
  };

  if (loading) {
    return (
      <Stack spacing={2} alignItems="center">
        <CircularProgress size={24} />
        <Typography variant="body2" color="text.secondary">
          Cargando permisos...
        </Typography>
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        {error}
        <Button size="small" onClick={loadPermissions}>
          Reintentar
        </Button>
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      {/* Tabla de permisos */}
      <TableContainer component={Paper} elevation={1}>
        <Table size="small" aria-label="tabla de permisos">
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell align="center">Rutas</TableCell>
              <TableCell align="center">Estado</TableCell>
              <TableCell>Fecha Creación</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {permissions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Stack spacing={1}>
                    <PermissionsIcon color="disabled" />
                    <Typography variant="body2" color="text.secondary">
                      No hay permisos registrados
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : (
              permissions.map((permission) => (
                <React.Fragment key={permission.id}>
                  <TableRow hover>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Typography variant="body2">
                          {permission.nombre}
                        </Typography>
                        {permission.nombre === "all" && (
                          <SystemIcon color="warning" />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {permission.descripcion || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={countPermissionRoutes(
                          permission.estructura_permisos,
                        )}
                        color="info"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        icon={
                          permission.activo ? <ActiveIcon /> : <InactiveIcon />
                        }
                        label={permission.activo ? "Activo" : "Inactivo"}
                        color={permission.activo ? "success" : "default"}
                        variant={permission.activo ? "filled" : "outlined"}
                      />
                    </TableCell>
                    <TableCell>
                      {formatDate(permission.fecha_creacion)}
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Ver estructura">
                          <IconButton
                            size="small"
                            onClick={() =>
                              setExpandedRow(
                                expandedRow === permission.id
                                  ? null
                                  : permission.id,
                              )
                            }
                            color="info"
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Editar permiso">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => onEdit(permission)}
                              color="primary"
                              disabled={permission.nombre === "all"}
                            >
                              <EditIcon />
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Tooltip title="Editar estructura JSON">
                          <IconButton
                            size="small"
                            onClick={() => onViewStructure(permission)}
                            color="secondary"
                          >
                            <JsonIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip
                          title={permission.activo ? "Desactivar" : "Activar"}
                        >
                          <span>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleToggleStatus(
                                  permission.id,
                                  permission.activo,
                                )
                              }
                              color={permission.activo ? "warning" : "success"}
                              disabled={permission.nombre === "all"}
                            >
                              {permission.activo ? (
                                <InactiveIcon />
                              ) : (
                                <ActiveIcon />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>

                        <Tooltip
                          title={
                            permission.nombre === "all"
                              ? "No se puede eliminar (Sistema)"
                              : "Eliminar permiso"
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(permission)}
                              color="error"
                              disabled={permission.nombre === "all"}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>

                  {/* Fila expandible con estructura de permisos */}
                  {expandedRow === permission.id && (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Accordion expanded>
                          <AccordionSummary>
                            <Typography variant="subtitle2">
                              Estructura de Permisos
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Box>
                              {renderPermissionStructure(
                                permission.estructura_permisos,
                              )}
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Diálogo de confirmación para eliminar */}
      <Dialog
        open={deleteDialog.open}
        onClose={() =>
          setDeleteDialog({
            open: false,
            permissionId: "",
            permissionName: "",
            isSystemPermission: false,
          })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            ¿Estás seguro de que deseas eliminar el permiso{" "}
            <strong>{deleteDialog.permissionName}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Esta acción no se puede deshacer y eliminará todas las relaciones
            con grupos.
          </Typography>
          {deleteDialog.isSystemPermission && (
            <Alert severity="error">
              Este es un permiso del sistema y no puede ser eliminado.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            size="small"
            onClick={() =>
              setDeleteDialog({
                open: false,
                permissionId: "",
                permissionName: "",
                isSystemPermission: false,
              })
            }
          >
            Cancelar
          </Button>
          <Button
            size="small"
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteDialog.isSystemPermission}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
